import { useId, useState } from 'react';
import { useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, SearchField, Switch } from '@ds/forms';
import { ExportMenu } from '@/comuns/ExportMenu';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { CategoriaDeProcedimento } from '@/data/cadastros';

function normalizar(texto: string): string {
    return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Categorias de procedimentos.
 *
 * Renomear leva os procedimentos junto. Excluir só a que ninguém usa: a que
 * está em uso se desativa, porque sumir com ela deixaria o procedimento com
 * um nome que nenhum cadastro reconhece.
 */
export function CategoriasDeProcedimentoAba({ categorias }: { categorias: CategoriaDeProcedimento[] }) {
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const idNome = useId();

    const [busca, setBusca] = useState('');
    const [alvo, setAlvo] = useState<CategoriaDeProcedimento | 'nova' | null>(null);
    const [nome, setNome] = useState('');
    const [ativa, setAtiva] = useState(true);
    const [excluindo, setExcluindo] = useState<CategoriaDeProcedimento | null>(null);
    const [salvando, setSalvando] = useState(false);

    const abrir = (c: CategoriaDeProcedimento | 'nova') => {
        setAlvo(c);
        setNome(c === 'nova' ? '' : c.nome);
        setAtiva(c === 'nova' ? true : c.ativa);
    };

    const executar = async (acao: () => Promise<void>, falha: string) => {
        setSalvando(true);
        try {
            await acao();
            await revalidator.revalidate();
        } catch (e) {
            aviso({ title: falha, description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    const salvar = () => executar(async () => {
        if (!alvo) return;
        await dados().salvarCategoriaDeProcedimento(alvo === 'nova' ? null : alvo.id, { nome, ativa });
        aviso({ title: alvo === 'nova' ? 'Categoria criada' : 'Categoria salva', description: nome.trim() });
        setAlvo(null);
    }, 'Não foi possível salvar a categoria');

    const excluir = () => executar(async () => {
        if (!excluindo) return;
        const { removida } = await dados().removerCategoriaDeProcedimento(excluindo.id);
        if (removida) aviso({ title: 'Categoria excluída', description: excluindo.nome });
        else aviso({ title: 'A categoria passou a ser usada', description: 'Desative-a em vez de excluir.', tone: 'warning' });
        setExcluindo(null);
    }, 'Não foi possível excluir a categoria');

    const desativar = () => executar(async () => {
        if (!excluindo) return;
        await dados().salvarCategoriaDeProcedimento(excluindo.id, { nome: excluindo.nome, ativa: false });
        aviso({ title: 'Categoria desativada', description: excluindo.nome });
        setExcluindo(null);
    }, 'Não foi possível desativar a categoria');

    const visiveis = categorias.filter((c) => normalizar(c.nome).includes(normalizar(busca.trim())));
    const sujo = alvo === 'nova' ? nome.trim() !== '' : alvo !== null && (nome !== alvo.nome || ativa !== alvo.ativa);
    const emUsoPeloAlvo = alvo !== null && alvo !== 'nova' ? alvo.procedimentos : 0;
    const renomeando = alvo !== null && alvo !== 'nova' && nome.trim() !== alvo.nome && emUsoPeloAlvo > 0;

    const colunas: DataTableColumn<CategoriaDeProcedimento>[] = [
        { key: 'nome', header: 'Categoria', render: (c) => <strong style={{ fontWeight: 600 }}>{c.nome}</strong> },
        { key: 'procedimentos', header: 'Procedimentos', align: 'right', width: 140 },
        { key: 'ativa', header: 'Situação', width: 120, render: (c) => <Badge tone={c.ativa ? 'success' : 'neutral'}>{c.ativa ? 'Ativa' : 'Desativada'}</Badge> },
        {
            key: 'acoes', header: '', width: 190, align: 'right',
            render: (c) => (
                <span style={{ display: 'inline-flex', gap: 6 }}>
                    <Button size="sm" variant="secondary" onClick={() => abrir(c)}>Editar</Button>
                    <Button size="sm" variant="ghost" onClick={() => setExcluindo(c)}>Excluir</Button>
                </span>
            ),
        },
    ];

    return (
        <>
            <Card
                title="Categorias de procedimentos" padding={0}
                subtitle="Procedimento com categoria nova cria a categoria aqui sozinho."
                actions={(
                    <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <ExportMenu
                            payload={{
                                title: 'Categorias de procedimentos',
                                columns: [
                                    { header: 'Categoria', value: (c: CategoriaDeProcedimento) => c.nome },
                                    { header: 'Procedimentos', value: (c: CategoriaDeProcedimento) => c.procedimentos },
                                    { header: 'Situação', value: (c: CategoriaDeProcedimento) => (c.ativa ? 'Ativa' : 'Desativada') },
                                ],
                                rows: visiveis,
                            }}
                        />
                        <Button size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => abrir('nova')}>Nova categoria</Button>
                    </span>
                )}
            >
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                    <SearchField value={busca} onChange={setBusca} placeholder="Buscar categoria" ariaLabel="Buscar categoria" />
                </div>
                <DataTable
                    columns={colunas} rows={visiveis} rowKey="id" stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage={busca ? `Nenhuma categoria com “${busca}”.` : 'Nenhuma categoria ainda.'}
                />
            </Card>

            <Modal
                open={alvo !== null} width={460} title={alvo === 'nova' ? 'Nova categoria' : 'Editar categoria'}
                onClose={() => setAlvo(null)} dirty={sujo} onSave={() => { void salvar(); }}
                footer={(
                    <>
                        <Button variant="ghost" onClick={() => setAlvo(null)}>Cancelar</Button>
                        <Button onClick={() => { void salvar(); }} disabled={!nome.trim() || salvando}>{salvando ? 'Salvando…' : 'Salvar'}</Button>
                    </>
                )}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <FormField
                        label="Nome" htmlFor={idNome} required
                        hint={renomeando ? `Os ${emUsoPeloAlvo} procedimentos desta categoria passam a usar o nome novo.` : undefined}
                    >
                        <Input id={idNome} value={nome} onChange={setNome} placeholder="Estética" />
                    </FormField>
                    <Switch checked={ativa} onChange={setAtiva} label="Ativa" description="Desativar só marca a categoria como fora de uso: os procedimentos que já a têm não mudam." />
                </div>
            </Modal>

            <Modal
                open={excluindo !== null} width={450} tone="danger"
                title={excluindo && excluindo.procedimentos > 0 ? 'Esta categoria está em uso' : 'Excluir categoria?'}
                description={excluindo?.nome}
                onClose={() => setExcluindo(null)}
                footer={(
                    <>
                        <Button variant="ghost" onClick={() => setExcluindo(null)}>Cancelar</Button>
                        {excluindo && excluindo.procedimentos > 0
                            ? <Button variant="danger" onClick={() => { void desativar(); }} disabled={salvando || !excluindo.ativa}>Desativar</Button>
                            : <Button variant="danger" onClick={() => { void excluir(); }} disabled={salvando}>Excluir</Button>}
                    </>
                )}
            >
                {excluindo && excluindo.procedimentos > 0 ? (
                    <Alert tone="warning" title={`${excluindo.procedimentos} ${excluindo.procedimentos === 1 ? 'procedimento usa' : 'procedimentos usam'} esta categoria`}>
                        Excluir deixaria esses procedimentos com uma categoria que não existe. Desative-a, ou mude a categoria deles antes.
                    </Alert>
                ) : (
                    <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)' }}>Nenhum procedimento usa esta categoria.</p>
                )}
            </Modal>
        </>
    );
}
